import { PICKER_PAGE_SIZE } from '@palaccount/contracts';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '@/api';
import { Ltr } from '@/components/amount';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { Modal } from '@/features/inventory/modal';
import { throwApiError } from '@/lib/server-messages';
import { useDebounced } from '@/lib/typeahead';

/**
 * INV-H7 — "Copy from…" on a NEW sales invoice: pick a quotation, a sales
 * order, another invoice or a purchase bill and its lines land in the grid.
 *
 * PURE CLIENT-SIDE COPY. The source is read through its own GET and mapped
 * here; nothing is linked, nothing on the source changes, and the existing
 * quote → order → invoice conversions (which DO link, and mark the source
 * converted) are untouched. This is a typing aid: what lands in the grid is
 * typed work from then on, exactly as if it had been keyed.
 *
 * The customer is offered only from a sales-side source — a bill's partner
 * is a supplier, and whether they are also a customer is INV-F10's question,
 * not this button's.
 */
export type CopySource = 'quotation' | 'salesOrder' | 'invoice' | 'bill';

export interface CopiedLine {
  itemId: string;
  warehouseId: string;
  unitId: string;
  description: string;
  qty: string;
  bonusQty: string;
  unitPrice: string;
  discountPct: string;
}

export interface CopiedDocument {
  /** The source's customer, or null when the source is a purchase bill. */
  partnerId: string | null;
  lines: CopiedLine[];
}

interface Row {
  id: string;
  code: string;
  name: string;
  date: string;
}

interface SourceLine {
  itemId: string | null;
  warehouseId?: string | null;
  unitId?: string | null;
  description: string;
  qty: string;
  bonusQty?: string;
  unitPrice: string;
  discountPct: string;
}

function copied(lines: readonly SourceLine[]): CopiedLine[] {
  return lines.map((line) => ({
    itemId: line.itemId ?? '',
    warehouseId: line.warehouseId ?? '',
    unitId: line.unitId ?? '',
    description: line.description,
    qty: line.qty,
    // A bill has no bonus column; a sales document's "0.000" is "none".
    bonusQty: line.bonusQty !== undefined && !/^0+(\.0+)?$/.test(line.bonusQty) ? line.bonusQty : '',
    unitPrice: line.unitPrice,
    discountPct: /^0+(\.0+)?$/.test(line.discountPct) ? '' : line.discountPct,
  }));
}

const PAGE = { page: 1, pageSize: PICKER_PAGE_SIZE } as const;

async function searchSource(source: CopySource, q: string): Promise<Row[]> {
  const term = q ? { q } : {};
  switch (source) {
    case 'quotation': {
      // The quotations list takes no search term: the first page is shown and
      // narrowed here, and an exact number goes through the lookup route.
      const r = await api.quotations.list({ query: PAGE });
      if (r.status !== 200) throwApiError(r.status, r.body);
      const needle = q.toLowerCase();
      const rows = r.body.quotations.map((d) => ({ id: d.id, code: d.code, name: d.partnerName, date: d.quotationDate }));
      return needle === ''
        ? rows
        : rows.filter((row) => row.code.toLowerCase().includes(needle) || row.name.toLowerCase().includes(needle));
    }
    case 'salesOrder': {
      const r = await api.salesOrders.list({ query: { ...term, ...PAGE } });
      if (r.status !== 200) throwApiError(r.status, r.body);
      return r.body.orders.map((d) => ({ id: d.id, code: d.code, name: d.partnerName, date: d.orderDate }));
    }
    case 'invoice': {
      const r = await api.invoices.list({ query: { ...term, ...PAGE } });
      if (r.status !== 200) throwApiError(r.status, r.body);
      return r.body.invoices.map((d) => ({ id: d.id, code: d.code, name: d.partnerName, date: d.invoiceDate }));
    }
    case 'bill': {
      const r = await api.purchaseInvoices.list({ query: { ...term, ...PAGE } });
      if (r.status !== 200) throwApiError(r.status, r.body);
      return r.body.bills.map((d) => ({ id: d.id, code: d.code, name: d.partnerName, date: d.invoiceDate }));
    }
  }
}

async function readSource(source: CopySource, id: string): Promise<CopiedDocument> {
  switch (source) {
    case 'quotation': {
      const r = await api.quotations.get({ params: { quotationId: id } });
      if (r.status !== 200) throwApiError(r.status, r.body);
      return { partnerId: r.body.partnerId, lines: copied(r.body.lines) };
    }
    case 'salesOrder': {
      const r = await api.salesOrders.get({ params: { orderId: id } });
      if (r.status !== 200) throwApiError(r.status, r.body);
      return { partnerId: r.body.partnerId, lines: copied(r.body.lines) };
    }
    case 'invoice': {
      const r = await api.invoices.get({ params: { invoiceId: id } });
      if (r.status !== 200) throwApiError(r.status, r.body);
      return { partnerId: r.body.partnerId, lines: copied(r.body.lines) };
    }
    case 'bill': {
      const r = await api.purchaseInvoices.get({ params: { billId: id } });
      if (r.status !== 200) throwApiError(r.status, r.body);
      return { partnerId: null, lines: copied(r.body.lines) };
    }
  }
}

const ALL_SOURCES: readonly CopySource[] = ['quotation', 'salesOrder', 'invoice', 'bill'];

export function CopyFromModal({
  onClose,
  onPick,
  sources = ALL_SOURCES,
  help,
}: {
  onClose: () => void;
  /** The mapped lines (and customer) — the editor decides how to adopt them. */
  onPick: (document: CopiedDocument) => void;
  /** Wave 4B: the kinds this editor offers (the quotation offers no purchase bill). */
  sources?: readonly CopySource[] | undefined;
  /** The help sentence, already translated; defaults to the invoice's. */
  help?: string | undefined;
}) {
  const { t } = useTranslation();
  const [source, setSource] = useState<CopySource>(sources[0] ?? 'quotation');
  const [q, setQ] = useState('');
  const search = useDebounced(q.trim());
  const [reading, setReading] = useState(false);
  const [failed, setFailed] = useState(false);

  const { data: rows, isPending, isError } = useQuery({
    queryKey: ['copy-from', source, search],
    queryFn: () => searchSource(source, search),
  });

  const pick = async (id: string) => {
    if (reading) return;
    setReading(true);
    setFailed(false);
    try {
      onPick(await readSource(source, id));
      onClose();
    } catch {
      setFailed(true);
    } finally {
      setReading(false);
    }
  };

  const labels: Record<CopySource, string> = {
    quotation: t('sales.copyFromKind.quotation'),
    salesOrder: t('sales.copyFromKind.salesOrder'),
    invoice: t('sales.copyFromKind.invoice'),
    bill: t('sales.copyFromKind.bill'),
  };

  return (
    <Modal title={t('sales.copyFromTitle')} onClose={onClose} className="max-w-xl">
      <div className="grid gap-3">
        <p className="text-sm text-muted-foreground">{help ?? t('sales.copyFromHelp')}</p>
        <div className="flex flex-wrap items-center gap-3">
          <NativeSelect
            aria-label={t('sales.copyFromSource')}
            value={source}
            onChange={(event) => {
              setSource(event.target.value as CopySource);
              setQ('');
            }}
            className="h-10 w-auto text-sm"
          >
            {sources.map((kind) => (
              <option key={kind} value={kind}>
                {labels[kind]}
              </option>
            ))}
          </NativeSelect>
          <Input
            type="search"
            aria-label={t('sales.copyFromSearch')}
            placeholder={t('docnav.searchPlaceholder')}
            value={q}
            onChange={(event) => setQ(event.target.value)}
            className="h-10 min-w-0 flex-1"
          />
        </div>
        {failed ? (
          <p role="alert" className="text-sm text-destructive">
            {t('common.loadFailed')}
          </p>
        ) : null}
        {isPending ? (
          <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy />
        ) : isError ? (
          <p role="alert" className="py-6 text-center text-sm text-destructive">
            {t('common.loadFailed')}
          </p>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t('docnav.noMatches')}</p>
        ) : (
          <ul className="max-h-80 divide-y overflow-y-auto rounded-md border" aria-busy={reading}>
            {rows.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  disabled={reading}
                  className="flex w-full items-center gap-3 px-3 py-2 text-start text-sm hover:bg-muted disabled:opacity-50"
                  onClick={() => void pick(row.id)}
                >
                  <Ltr value={row.code} className="w-28 shrink-0 font-mono text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate font-medium">{row.name}</span>
                  <Ltr value={row.date} className="shrink-0 font-mono text-xs text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

/**
 * Wave 4B — "From quotation…" on a NEW sales order. Unlike Copy from, this is
 * the LINKING conversion: the pick calls the existing `convertToOrder` route,
 * which makes a draft order carrying the quotation's lines, reference, terms
 * and salesperson, records "From QUO-####", and marks the quotation
 * converted — then the editor opens the new order.
 *
 * Offered: the chosen customer's quotations the server will convert —
 * accepted, or expired (R4: expiry never blocks; BR-SAL-01-14). A sent
 * quotation is not offered because the server refuses it (it has not been
 * accepted yet), and a picker offering what the door refuses is a trap.
 */
export function QuotationConvertPicker({
  partnerId,
  onClose,
  onPick,
  busy,
  error,
}: {
  /** '' = every customer's (the picker may open before a customer is chosen). */
  partnerId: string;
  onClose: () => void;
  onPick: (quotationId: string) => void;
  busy: boolean;
  error: string | null;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const search = useDebounced(q.trim());
  const { data: rows, isPending, isError } = useQuery({
    queryKey: ['quote-convert-picker', partnerId, search],
    queryFn: async (): Promise<Row[]> => {
      const found: Row[] = [];
      for (const status of ['accepted', 'expired'] as const) {
        const r = await api.quotations.list({
          query: {
            ...PAGE,
            status,
            ...(partnerId ? { partnerId } : {}),
            ...(search ? { q: search } : {}),
          },
        });
        if (r.status !== 200) throwApiError(r.status, r.body);
        found.push(
          ...r.body.quotations.map((d) => ({ id: d.id, code: d.code, name: d.partnerName, date: d.quotationDate })),
        );
      }
      return found;
    },
  });

  return (
    <Modal title={t('orders.fromQuotationTitle')} onClose={onClose} className="max-w-xl">
      <div className="grid gap-3">
        <p className="text-sm text-muted-foreground">{t('orders.fromQuotationHelp')}</p>
        <Input
          type="search"
          aria-label={t('sales.copyFromSearch')}
          placeholder={t('docnav.searchPlaceholder')}
          value={q}
          onChange={(event) => setQ(event.target.value)}
          className="h-10"
        />
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        {isPending ? (
          <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy />
        ) : isError ? (
          <p role="alert" className="py-6 text-center text-sm text-destructive">
            {t('common.loadFailed')}
          </p>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t('docnav.noMatches')}</p>
        ) : (
          <ul className="max-h-80 divide-y overflow-y-auto rounded-md border" aria-busy={busy}>
            {rows.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  disabled={busy}
                  className="flex w-full items-center gap-3 px-3 py-2 text-start text-sm hover:bg-muted disabled:opacity-50"
                  onClick={() => onPick(row.id)}
                >
                  <Ltr value={row.code} className="w-28 shrink-0 font-mono text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate font-medium">{row.name}</span>
                  <Ltr value={row.date} className="shrink-0 font-mono text-xs text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
